import { useCallback, useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"

//Housing API and components
import myStyle from "../../../styles/housing/HousingActivities.module.css"
import { housingApiListActivitiesPost } from "../housingApi"
import HousingActivitiesDetail from "./HousingActivitiesDetail"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//SOAR Components
import { LoaderMedium } from "../../../components/Loaders/index"

//Helpers
import { LogError, LogWarning } from "../../../helpers/logger"
import HousingActivitiesFilter from "./HousingActivitiesFilter"
import HousingActivitiesForm from "./HousingActivitiesForm"

//PrimeReact
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Tooltip } from "primereact/tooltip"

export function HousingActivities({
  participant,
  houseParticipants,
  activeHouseParticipants,
  onSetParticipant,
  users,
  houses,
}) {
  //Retrieve relevant context values
  const userCtx = useContext(UserContext)

  let startDate = new Date()
  startDate.setDate(1)
  startDate.setMonth(startDate.getMonth() - 1)

  const [activityFilters, setActivityFilters] = useState({
    type: "",
    houseId: "",
    fromDate: startDate,
    toDate: new Date(),
    participantId: "",
  })

  const [activities, setActivities] = useState([])

  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editActivity, setEditActivity] = useState(undefined)
  const [detailData, setDetailData] = useState(undefined)
  const [showDetail, setShowDetail] = useState(false)
  const [pageSetup, setPageSetup] = useState({ rows: 20, first: 0, page: 0 })

  const getActivityList = useCallback(async () => {
    try {
      setIsLoading(true)

      const startDate = DateTime.fromJSDate(activityFilters.fromDate)
        .toUTC()
        .toISODate()

      const endDate = DateTime.fromJSDate(activityFilters.toDate)
        .set({ hour: 23 })
        .set({ minute: 59 })
        .set({ second: 59 })
        .toUTC()
        .toISO()

      let result = await housingApiListActivitiesPost(userCtx.tokenHousing, {
        enterpriseId: userCtx.session.user.enterprise_id,
        organizationId: userCtx.session.user.organization_id,
        participantId: activityFilters.participantId,
        typeFilter: activityFilters.type,
        houseId: activityFilters.houseId,
        start: startDate,
        end: endDate,
        PageSize: pageSetup.rows,
        PageNum: pageSetup.page + 1,
      })

      if (result?.activities.length > 0) {
        result.activities.forEach((activity) => {
          activity.displayType =
            activity.activity.activityType === "MedicineManagement"
              ? "Medicine Management"
              : activity.activity.activityType === "SubstanceTest"
              ? "Substance testing"
              : "Other"

          activity.displayNote =
            activity.activity.notes.length > 50
              ? activity.activity.notes.substring(0, 50) + "..."
              : activity.activity.notes

          const participant = houseParticipants.find(
            (participant) =>
              participant.participant_id === activity.activity.participantId
          )

          activity.participantName = !!participant
            ? participant.name2
            : `Unknown participant`

          activity.participantStatusActive = !!participant
            ? participant.statusActive
            : true
        })
      }

      setActivities(result)

      setIsLoading(false)
    } catch (error) {
      LogError("Unable to retrieve Housing activity list", error)
      userCtx.setNotificationError("Unable to retrieve activities")
      setActivities([])
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activityFilters.fromDate,
    activityFilters.toDate,
    activityFilters.participantId,
    activityFilters.type,
    activityFilters.houseId,
    userCtx.tokenHousing,
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
    pageSetup.rows,
    pageSetup.page,
    houseParticipants,
  ])

  useEffect(() => {
    getActivityList()
  }, [getActivityList])

  const DateColumnTemplate = (rowData) => {
    let date = rowData.activity.startTime || rowData.activity.creationDate

    if (!!date) {
      date = DateTime.fromISO(date, { zone: "utc" }).toLocal().toLocaleString()
    }

    return <>{date}</>
  }

  const GetDisplayText = (text, maxSize) => {
    if (!!maxSize && !!text) {
      const returnValue =
        text.length > maxSize ? text.substring(0, maxSize - 3) + "..." : text
      return returnValue
    } else {
      return text
    }
  }

  const GetMedicineAdditionalInfo = (activity, isDialog) => {
    if (!!activity.medicationManagement) {
      let frequency = activity.medicationManagement.dispensingFrequency
      if (frequency === "other") {
        frequency = activity.medicationManagement.dispensingFrequencyOther
      }

      const isWeekly = frequency === "Weekly"
      const isMonthly = frequency === "Monthly"

      const maxTextSize = !!isDialog ? undefined : 20

      return (
        <div className={!!isDialog ? "" : myStyle.additionalInfoContainer}>
          <div>
            <span className={myStyle.additionalInfoHeader}>Type: </span>
            {GetDisplayText(activity.medicationManagement.type, maxTextSize)}
          </div>
          <div>
            <span className={myStyle.additionalInfoHeader}>Storage: </span>
            {GetDisplayText(activity.medicationManagement.storage, maxTextSize)}
          </div>
          <div>
            <span className={myStyle.additionalInfoHeader}>Dispensing: </span>
            {GetDisplayText(
              activity.medicationManagement.dispensing,
              maxTextSize
            )}
          </div>
          <div>
            <span className={myStyle.additionalInfoHeader}>Frequency: </span>
            {GetDisplayText(frequency, maxTextSize)}
          </div>
          {isDialog && (isWeekly || isMonthly) ? (
            <div>
              <span className={myStyle.additionalInfoHeader}>
                {isWeekly ? "Day of week" : "Day of month"}:{" "}
              </span>
              {GetDisplayText(
                activity.medicationManagement.dispensingFrequencyInterval,
                maxTextSize
              )}
            </div>
          ) : null}
        </div>
      )
    } else {
      return <>Not available</>
    }
  }

  const GetSubstanceAdditionalInfo = (activity, isDialog) => {
    const substanceTest = activity.substanceTest
    const maxTextSize = !!isDialog ? undefined : 20 //undefines == show all text

    const testType =
      substanceTest.type === "Other"
        ? substanceTest.typeOther
        : substanceTest.type

    const testOutcome =
      substanceTest.outcome === "Other"
        ? substanceTest.outcomeOther
        : substanceTest.outcome

    const testResultColor =
      substanceTest.result === "positive"
        ? "red"
        : substanceTest.result === "negative"
        ? "green"
        : "orange"

    const substance = activity.substanceTestSubstance
      .map((substance) => {
        return substance.substanceName
      })
      .join(";")

    return (
      <div className={!!isDialog ? "" : myStyle.additionalInfoContainer}>
        <div>
          <span className={myStyle.additionalInfoHeader}>Type: </span>
          {GetDisplayText(testType, maxTextSize)}
        </div>
        <div>
          <span className={myStyle.additionalInfoHeader}>Result: </span>
          <span style={{ color: testResultColor }}>
            {GetDisplayText(substanceTest.result, maxTextSize)}
          </span>
        </div>
        {substanceTest.result === "positive" ? (
          <>
            <div>
              <span className={myStyle.additionalInfoHeader}>Substance: </span>
              {GetDisplayText(substance, maxTextSize)}
            </div>
            <div>
              <span className={myStyle.additionalInfoHeader}>Outcome: </span>
              {GetDisplayText(testOutcome, maxTextSize)}
            </div>
          </>
        ) : null}
      </div>
    )
  }

  const AdditionalInfoTemplate = (rowData, isDialog) => {
    let component = null
    const maxTextSize = !!isDialog ? undefined : 20

    if (rowData.activity.activityType === "MedicineManagement") {
      component = GetMedicineAdditionalInfo(rowData, isDialog)
    } else if (rowData.activity.activityType === "SubstanceTest") {
      component = GetSubstanceAdditionalInfo(rowData, isDialog)
    } else {
      component = (
        <div className={!!isDialog ? "" : myStyle.additionalInfoContainer}>
          <div>
            <span className={myStyle.additionalInfoHeader}>Type: </span>
            {GetDisplayText(rowData.other.otherActivity, maxTextSize)}
          </div>
        </div>
      )
    }

    return component
  }

  const ActivityDetailTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Tooltip target=".house-activity-t-tip" />
        <button
          className={`btn ${myStyle.filterHeaderButton} house-activity-t-tip`}
          data-pr-tooltip="View additional information for this activity"
          onClick={() => {
            setDetailData(rowData)
            setShowDetail(true)
          }}
        >
          <span className={`material-icons`}>{"info"}</span>
        </button>
        <button
          className={`btn ${myStyle.filterHeaderButton} house-activity-t-tip`}
          data-pr-tooltip="Edit this activity"
          onClick={() => {
            setEditActivity(rowData)
            setShowForm(true)
          }}
          disabled={!rowData.participantStatusActive}
        >
          <span className={`material-icons`}>{"edit"}</span>
        </button>
      </div>
    )
  }

  const UserColumnTemplate = (rowData) => {
    let userName = rowData.activity.userId

    const user = users?.find((user) => user.staff_id === userName)

    if (!!user) {
      userName = `${user.staff_name_first} ${user.staff_name_last}`
    } else if (rowData.userId === userCtx.session.user.staff_id) {
      //Get current user details
      userName = `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`
    } else {
      LogWarning(`Unable to find user: ${rowData.userId}`)
      userName = "Unknown"
    }

    return <>{userName}</>
  }

  return (
    <>
      <HousingActivitiesFilter
        isLoading={isLoading}
        onRefreshClick={() => {
          getActivityList()
        }}
        onNewClick={() => {
          setEditActivity(undefined)
          setShowForm(true)
        }}
        onChangeFilters={(filters) => {
          setActivityFilters(filters)
        }}
        houseList={houses}
        participants={houseParticipants}
        participant={participant}
        onSetParticipant={onSetParticipant}
      />
      <HousingActivitiesForm
        showForm={showForm}
        activityProfile={editActivity}
        onHideForm={() => {
          setEditActivity(undefined)
          setShowForm(false)
        }}
        onActivityCreated={(participant) => {
          onSetParticipant(participant)
          getActivityList()
          //setRefreshActivities(!refreshActivities)
          setShowForm(false)
        }}
        participant={participant}
        houseParticipants={houseParticipants}
        activeHouseParticipants={activeHouseParticipants}
        houses={houses}
      />
      <HousingActivitiesDetail
        activity={detailData}
        showForm={showDetail}
        onHideForm={() => {
          setShowDetail(false)
        }}
        onDateDisplay={DateColumnTemplate}
        userList={users}
        onGetAdditionalInfo={(data) => AdditionalInfoTemplate(data, true)}
      />

      {isLoading ? (
        <LoaderMedium />
      ) : (
        <DataTable
          value={activities.activities}
          lazy
          paginator
          first={(activities.pageNum - 1) * activities.pageSize}
          rows={activities.pageSize}
          totalRecords={activities.numTotal}
          onPage={(setup) => {
            setPageSetup(setup)
          }}
          disabled={isLoading}
        >
          <Column
            body={DateColumnTemplate}
            header="Date"
            style={{ width: "80px" }}
          />
          <Column header="Staff" body={UserColumnTemplate} />
          <Column
            field={"participantName"}
            header={"Participant"}
            style={{
              minWidth: "150px",
              maxWidth: "200px",
              wordBreak: "break-word",
            }}
          />
          <Column
            field={"activity.house.name"}
            header="House"
            style={{
              minWidth: "150px",
              maxWidth: "200px",
            }}
          />
          <Column field="displayType" header="Type" />
          <Column
            body={(rowData) => {
              return AdditionalInfoTemplate(rowData, false)
            }}
            header="Additional information"
          />
          <Column
            body={ActivityDetailTemplate}
            header=""
            style={{ width: "80px" }}
          />
        </DataTable>
      )}
    </>
  )
}
